import type { TeacherScenario, TeacherScenarioMode } from "@/config/teacherScenarios";
import { extractTitleFromMarkdown, openMarkdownPrintWindow, copyMarkdownAsRichText } from "@/lib/markdownPrint";
import {
  buildDebriefTranscript,
  buildPrincipalTakeawayPrompt,
  extractAssistantMarkdown,
  sendTeacherScenarioMessage,
  type ScenarioChatTurn,
  type TeacherScenarioSessionContext,
} from "@/services/teacherScenarioService";
import type { UserSession } from "@/types/session";
import type { WorkspacePersona } from "@/config/workspacePersona";

const STORAGE_PREFIX = "heybodhi:saved-scenario-takeaways:v1:";
export const savedScenarioTakeawaysChangedEvent = "heybodhi:saved-scenario-takeaways-changed";

export interface SavedScenarioTakeaway {
  id: string;
  scenarioId: string;
  scenarioTitle: string;
  mode: TeacherScenarioMode;
  title: string;
  excerpt: string;
  markdown: string;
  createdAt: string;
}

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function readAll(userId: string): SavedScenarioTakeaway[] {
  if (typeof window === "undefined" || !userId) return [];
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is SavedScenarioTakeaway =>
        typeof x === "object" &&
        x !== null &&
        typeof (x as SavedScenarioTakeaway).id === "string" &&
        typeof (x as SavedScenarioTakeaway).markdown === "string",
    );
  } catch {
    return [];
  }
}

function writeAll(userId: string, rows: SavedScenarioTakeaway[]): void {
  if (typeof window === "undefined" || !userId) return;
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(rows));
  } catch {
    /* quota */
  }
}

function notifyChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(savedScenarioTakeawaysChangedEvent));
}

function pickExcerpt(markdown: string): string {
  for (const raw of markdown.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || line.startsWith("#")) continue;
    return line.replace(/[*_`]+/g, "").slice(0, 220);
  }
  return "Scenario practice takeaway for leadership.";
}

export function listSavedScenarioTakeaways(userId: string | null | undefined): SavedScenarioTakeaway[] {
  if (!userId) return [];
  return readAll(userId).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

export function saveScenarioTakeaway(
  userId: string,
  input: {
    scenarioId: string;
    scenarioTitle: string;
    mode: TeacherScenarioMode;
    markdown: string;
  },
): SavedScenarioTakeaway {
  const row: SavedScenarioTakeaway = {
    id: `st-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    scenarioId: input.scenarioId,
    scenarioTitle: input.scenarioTitle,
    mode: input.mode,
    title: extractTitleFromMarkdown(input.markdown),
    excerpt: pickExcerpt(input.markdown),
    markdown: input.markdown,
    createdAt: new Date().toISOString(),
  };
  const rows = readAll(userId);
  rows.unshift(row);
  writeAll(userId, rows);
  notifyChanged();
  return row;
}

export function deleteSavedScenarioTakeaway(userId: string, id: string): void {
  writeAll(
    userId,
    readAll(userId).filter((r) => r.id !== id),
  );
  notifyChanged();
}

export async function generatePrincipalTakeaway({
  scenario,
  mode,
  messages,
  session,
  workspacePersona,
}: {
  scenario: TeacherScenario;
  mode: TeacherScenarioMode;
  messages: ScenarioChatTurn[];
  session: UserSession;
  workspacePersona: WorkspacePersona;
}): Promise<string> {
  const transcript = buildDebriefTranscript(messages, scenario, mode);
  const ctx: TeacherScenarioSessionContext = {
    scenarioId: scenario.id,
    mode,
    stage: "debrief",
  };
  const response = await sendTeacherScenarioMessage({
    message: buildPrincipalTakeawayPrompt(scenario, mode, transcript),
    session,
    workspacePersona,
    scenario,
    ctx,
  });
  return extractAssistantMarkdown(response);
}

export function openTakeawayPrintWindow(
  markdown: string,
  scenarioTitle?: string,
  authorName?: string,
): boolean {
  return openMarkdownPrintWindow(markdown, {
    title: scenarioTitle ?? extractTitleFromMarkdown(markdown),
    metaLabel: "Scenario Practice Takeaway",
    productLine: "Teacher Scenario Practice",
    authorName,
  });
}

export async function copyTakeawayToClipboard(markdown: string, authorName?: string): Promise<boolean> {
  return copyMarkdownAsRichText(markdown, markdown, {
    metaLabel: "Scenario Practice Takeaway",
    productLine: "Teacher Scenario Practice",
    authorName,
  });
}

export { buildDebriefTranscript };
